import React, { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, TextField, Button, Alert, CircularProgress, Link, Typography, FormControlLabel, Checkbox } from '@mui/material'
import { Trans } from 'react-i18next'
import { useTranslation } from 'react-i18next'
import { pendingInviteRoute, takePendingInvite } from '@/lib/pendingInvite'
import { useAuth } from '@/contexts/AuthContext'
import AuthShell from '@/components/AuthShell'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'
import type { RegisterCredentials } from '@/types/auth'

export default function RegisterPage() {
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    accepted_terms: false,
  })
  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { register } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const { t: tErrors } = useTranslation('errors')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (error) setError('')
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({
      ...prev,
      password_confirm: e.target.value,
    }))
    if (error) setError('')
  }

  const validateForm = (): string | null => {
    if (!credentials.email.trim()) {
      return t('emailRequired')
    }
    if (!credentials.password) {
      return t('passwordRequired')
    }
    if (credentials.password.length < 8) {
      return t('passwordMinLength')
    }
    if (credentials.password !== credentials.password_confirm) {
      return t('passwordsDoNotMatch')
    }
    if (!credentials.accepted_terms) {
      return t('termsRequired')
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await register(credentials)
      const invite = takePendingInvite()
      navigate(invite ? pendingInviteRoute(invite) : '/')
    } catch (err) {
      setError(resolveApiErrorMessage(err, tErrors))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell title={t('createAccount')} subtitle={t('createAccountSubtitle')}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t('emailAddress')}
          name="email"
          autoComplete="email"
          autoFocus
          value={credentials.email}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            margin="normal"
            fullWidth
            id="first_name"
            label={t('firstName')}
            name="first_name"
            autoComplete="given-name"
            value={credentials.first_name}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <TextField
            margin="normal"
            fullWidth
            id="last_name"
            label={t('lastName')}
            name="last_name"
            autoComplete="family-name"
            value={credentials.last_name}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </Box>
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('password')}
          type="password"
          id="password"
          autoComplete="new-password"
          value={credentials.password}
          onChange={handleChange}
          disabled={isSubmitting}
          helperText={t('passwordHint')}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password_confirm"
          label={t('confirmPassword')}
          type="password"
          id="password_confirm"
          autoComplete="new-password"
          value={credentials.password_confirm}
          onChange={handleConfirmPasswordChange}
          disabled={isSubmitting}
        />
        <FormControlLabel
          sx={{ mt: 1.5, alignItems: 'flex-start' }}
          control={
            <Checkbox
              name="accepted_terms"
              checked={credentials.accepted_terms}
              onChange={(e) => {
                setCredentials((prev) => ({ ...prev, accepted_terms: e.target.checked }))
                if (error) setError('')
              }}
              disabled={isSubmitting}
              sx={{ pt: 0.25 }}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              <Trans
                t={t}
                i18nKey="acceptTerms"
                components={{
                  termsLink: (
                    <Link
                      href="/docs/regulamin_facewise.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      fontWeight={600}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ),
                }}
              />
            </Typography>
          }
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2.5, mb: 2, py: 1.25 }}
          disabled={isSubmitting || !credentials.accepted_terms}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('createAccount')}
        </Button>
        <Typography variant="body2" align="center" color="text.secondary">
          {t('haveAccount')}{' '}
          <Link component={RouterLink} to="/login" underline="hover" fontWeight={600}>
            {t('signInHere')}
          </Link>
        </Typography>
      </Box>
    </AuthShell>
  )
}
